import { ControllerResponse } from '@/DTOS/Controller/ControllerResponse';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, App, Button, Spin, Table } from 'antd';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import React from 'react';
import KPIService from '../../services/kpi/KPIService';
import { getBaseUrl } from '../../utils/baseUrl';
import { saveBlobCrossPlatform } from '../../utils/saveBlobCrossPlatform';
import './KPIArhive.css';

dayjs.extend(weekOfYear);

interface KPIRecord {
  id: string;
  date: string;
  value: string;
  kpiDefineId: string;
  kpiDefineLabel: string;
  kpiDefineAction: string;
  kpiDefineSection: string;
  kpiDefineLabelOrder: number;
  kpiDefineIsLastDay: boolean;
}

interface KPIData {
  id: string;
  date: string;
  weeks: {
    currents: KPIRecord[];
  };
}

const KPIArhive: React.FC = () => {
  const { message } = App.useApp();
  const {
    data: kpiData,
    isFetching: isKPILoading,
    isError: isErrorOnFetchKPI,
    error: kpiError,
  } = useQuery({
    queryKey: ['kpiArchive'],
    queryFn: () => {
      return KPIService.search({}).then((data: ControllerResponse<KPIData[]>) => {
        return data?.response || [];
      });
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  // const { mutate: printPdf, isPending: isPrinting } = useMutation({
  //   mutationFn: async (id: string) => {
  //     setPrintingId(id);
  //     try {
  //       const response = await KPIService.printWeek(id);
  //       return response;
  //     } finally {
  //       setPrintingId(null);
  //     }
  //   },
  //   onSuccess: (response: any) => {
  //     if (response instanceof Blob) {
  //       const url = window.URL.createObjectURL(response);
  //       const a = document.createElement('a');
  //       a.href = url;
  //       a.download = `KPI_Weekly_Report_${dayjs().format('YYYY-MM-DD')}.pdf`;
  //       document.body.appendChild(a);
  //       a.click();
  //       document.body.removeChild(a);
  //       window.URL.revokeObjectURL(url);
  //       message.success('Report downloaded successfully');
  //     } else {
  //       console.error('Invalid response format:', response);
  //       message.error('Invalid response format');
  //     }
  //   },
  //   onError: (error: any) => {
  //     console.error('Download error:', error);
  //     message.error('Failed to download report: ' + (error.message || 'Unknown error'));
  //   },
  // });

  const preview = (record: any) => {
    // In APK mode, treat 'view' as 'download' since APK can't handle browser previews
    const w = window as any;
    const action = w.AndroidInterface && typeof w.AndroidInterface.saveBlobFile === 'function' ? 'download' : 'view';
    printPdf({ record, action });
  };

  // const handleView = async (id: string) => {
  //   try {
  //     const response = await fetch(`${getBaseUrl()}/kpiRecord/printWeek/${id}`, {
  //       method: 'GET',
  //       headers: {
  //         'Authorization': 'Bearer ' + localStorage.getItem('authToken'),
  //         'Accept': 'application/pdf'
  //       },
  //       credentials: 'include'
  //     });

  //     if (!response.ok) {
  //       throw new Error('Failed to load PDF');
  //     }

  //     const blob = await response.blob();
  //     const url = URL.createObjectURL(blob);
  //     window.open(url, '_blank');
  //     // Clean up the URL after a delay to ensure it's loaded in the new tab
  //     setTimeout(() => URL.revokeObjectURL(url), 1000);
  //   } catch (error: any) {
  //     message.error('Failed to load PDF: ' + (error.message || 'Unknown error'));
  //   }
  // };

  const { mutate: printPdf, isPending: isPrinting } = useMutation<
    Response,
    Error,
    { record: any; action: 'view' | 'download' }
  >({
    mutationFn: async ({ record, action }) => {
      const fileUrl = `${getBaseUrl()}/kpiRecord/printWeek/${record}`;

      if (action === 'view') {
        // Open the file directly in the browser
        window.open(fileUrl, '_blank');
        return new Response(null, { status: 200 }); // Return a dummy response since we are redirecting
      }

      // If action is 'download', fetch the file
      return await KPIService.printWeek(record.id);
    },
    onSuccess: async (response, { action }) => {
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }

      if (action === 'download') {
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'file.pdf';

        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }

        const blob = await response.blob();
        saveBlobCrossPlatform(blob, filename);
      }
    },
    onError: (error) => {
      console.error('Error processing your request:', error);
    },
  });

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Week of Year',
      dataIndex: 'date',
      key: 'week',
      render: (date: string) => `Week ${dayjs(date).week()}`,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: KPIData) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            type='primary'
            onClick={() => printPdf({ record: record, action: 'download' })}
            loading={isPrinting}
            disabled={isPrinting}
          >
            Print Week
          </Button>
          <Button onClick={() => preview(record.id)} disabled={isPrinting}>
            View Week
          </Button>
        </div>
      ),
    },
  ];

  if (isKPILoading || isPrinting) {
    return <Spin size='large' className='spinner' />;
  }

  if (isErrorOnFetchKPI) {
    return <Alert message={kpiError.message} type='error' />;
  }

  return (
    <div className='kpiArchiveWrap'>
      <Table
        dataSource={kpiData || []}
        columns={columns}
        rowKey='id'
        pagination={false}
        size='middle'
        bordered
        className='kpi-archive-table'
      />
    </div>
  );
};

export default KPIArhive;
